import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const StaffPINLogin = () => {
  const [pin, setPin] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];
  const navigate = useNavigate();

  useEffect(() => {
    // Focus first input on mount
    inputRefs[0].current?.focus();
  }, []);

  const handlePinChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }

    // Auto-submit when all 4 digits entered
    if (index === 3 && value) {
      const fullPin = newPin.join('');
      if (fullPin.length === 4) {
        handleSubmit(fullPin);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4);
    
    if (/^\d+$/.test(pastedData)) {
      const newPin = pastedData.split('');
      setPin([
        newPin[0] || '',
        newPin[1] || '',
        newPin[2] || '',
        newPin[3] || ''
      ]);

      // Focus last filled input or submit
      if (pastedData.length === 4) {
        handleSubmit(pastedData);
      } else {
        inputRefs[pastedData.length]?.current?.focus();
      }
    }
  };

  const getRestaurantId = () => {
    // Try to get restaurant ID from URL params
    const urlParams = new URLSearchParams(window.location.search);
    let restaurantId = urlParams.get('restaurant_id');
    
    if (restaurantId) {
      return restaurantId;
    }
    
    // Try localStorage
    restaurantId = localStorage.getItem('current_restaurant_id');
    if (restaurantId) {
      return restaurantId;
    }
    
    // Try to extract from any existing token
    const token = localStorage.getItem('token');
    if (token && token !== 'staff_authenticated') {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.restaurant_id) {
          return payload.restaurant_id;
        }
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    }
    
    // Fallback: Use default restaurant ID for demo
    // In production, this should be configured per restaurant/domain
    return '1ae4f961-6b9c-4032-ad91-0e7a434e632a';
  };

  const handleSubmit = async (pinCode) => {
    const fullPin = pinCode || pin.join('');
    
    if (fullPin.length !== 4) {
      setError('Please enter a 4-digit PIN');
      toast.error('Please enter a 4-digit PIN');
      return;
    }

    const restaurantId = getRestaurantId();
    console.log('Restaurant ID for login:', restaurantId);
    
    if (!restaurantId) {
      setError('Restaurant not found. Please contact administrator.');
      toast.error('Restaurant not found. Please contact administrator.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Attempting staff login with PIN:', fullPin, 'Restaurant:', restaurantId);
      const response = await axios.post(`${API}/api/rbac/staff/login`, {
        pin: fullPin,
        restaurant_id: restaurantId
      });
      
      console.log('Login response:', response.data);

      // Extract data from response
      const { access_token, staff, permissions, restaurant_id } = response.data;
      
      // Store staff session for reference
      const staffSession = {
        staff,
        permissions,
        restaurant_id,
        loginTime: new Date().toISOString(),
        isStaffLogin: true
      };
      
      localStorage.setItem('staff_token', JSON.stringify(staffSession));
      
      // Store the actual JWT token for API calls
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify({
        id: staff.id,
        name: staff.name,
        email: staff.email,
        role: 'staff',
        restaurant_id: restaurant_id,
        permissions: permissions
      }));

      toast.success(`Welcome back, ${staff.name}!`);
      
      // Force reload to trigger auth context update
      window.location.href = '/pos';
    } catch (error) {
      console.error('PIN login error:', error);
      console.error('Error details:', error.response?.data);
      
      let errorMsg = 'Login failed. Please try again.';
      
      if (error.response?.status === 401) {
        errorMsg = 'Invalid PIN. Please try again.';
      } else if (error.response?.status === 403) {
        errorMsg = error.response?.data?.detail || 'Access denied';
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      
      setError(errorMsg);
      toast.error(errorMsg);
      
      // Clear PIN on error
      setPin(['', '', '', '']);
      inputRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setPin(['', '', '', '']);
    inputRefs[0].current?.focus();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
            <Lock className="w-10 h-10 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Staff Login</h1>
          <p className="text-gray-600">Enter your 4-digit PIN to access POS</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800 text-center">{error}</p>
          </div>
        )}

        {/* PIN Input */}
        <div className="mb-8">
          <label className="block text-sm font-semibold text-gray-700 mb-4 text-center">
            Enter PIN
          </label>
          <div className="flex gap-4 justify-center">
            {pin.map((digit, index) => (
              <input
                key={index}
                ref={inputRefs[index]}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="w-16 h-16 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                disabled={loading}
              />
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3">
          <button
            onClick={() => handleSubmit()}
            disabled={loading || pin.join('').length !== 4}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span>Verifying...</span>
            ) : (
              <>
                <span>Login</span>
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
          
          <button
            onClick={handleClear}
            disabled={loading}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-lg font-semibold transition disabled:opacity-50"
          >
            Clear
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Don't have a PIN? Contact your restaurant manager.
          </p>
        </div>

        {/* Back to Admin Login */}
        <div className="mt-4 text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
          >
            Admin Login →
          </button>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-10 left-10 w-20 h-20 bg-white opacity-10 rounded-full blur-xl"></div>
      <div className="absolute bottom-10 right-10 w-32 h-32 bg-white opacity-10 rounded-full blur-xl"></div>
    </div>
  );
};

export default StaffPINLogin;
